/**
 * Extracts YouTube video ID from various YouTube URL formats
 * Supports formats like:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Regular expression to match various YouTube URL formats
  const patterns = [
    // Standard YouTube watch URLs (with proper domain boundaries)
    /(?:^|[^a-zA-Z0-9-])(?:www\.)?youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    // Mobile YouTube watch URLs
    /(?:^|[^a-zA-Z0-9-])m\.youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    // YouTube short URLs
    /(?:^|[^a-zA-Z0-9-])youtu\.be\/([^&\n?#]+)/,
    // YouTube embed URLs
    /(?:^|[^a-zA-Z0-9-])(?:www\.)?youtube\.com\/embed\/([^&\n?#]+)/,
    // YouTube v URLs
    /(?:^|[^a-zA-Z0-9-])(?:www\.)?youtube\.com\/v\/([^&\n?#]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      // Additional validation: YouTube video IDs are typically 11 characters
      const videoId = match[1];
      if (videoId.length === 11 && /^[a-zA-Z0-9_-]+$/.test(videoId)) {
        return videoId;
      }
    }
  }

  return null;
}

/**
 * Generates YouTube thumbnail URL from video ID
 */
export function getYouTubeThumbnailUrl(
  videoId: string,
  quality:
    | "default"
    | "mqdefault"
    | "hqdefault"
    | "sddefault"
    | "maxresdefault" = "mqdefault"
): string {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

/**
 * Validates if a string is a valid YouTube URL
 */
export function isValidYouTubeUrl(url: string): boolean {
  return extractYouTubeVideoId(url) !== null;
}
